import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import Parse from 'parse';

const inputSx = {
  backgroundColor: '#2B2B2B',
  '& .MuiOutlinedInput-root': { color: '#fff' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }
};

/**
 * Create / edit a display currency (catalogue row).
 * Code: 1–3 chars, uppercase, [A-Z0-9]. internalCurrency: SC | GC.
 */
const CreateEditCurrencyDialog = ({ open, onClose, onSaved, currency, mode }) => {
  const isEdit = mode === 'edit';
  const [code, setCode] = useState('');
  const [internalCurrency, setInternalCurrency] = useState('SC');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCode(currency?.code || '');
      setInternalCurrency(currency?.internalCurrency || 'SC');
      setName(currency?.name || '');
      setIsActive(currency ? currency.isActive !== false : true);
      setError('');
    }
  }, [open, currency]);

  const handleCodeChange = (e) => {
    const v = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 3);
    setCode(v);
  };

  const handleSave = async () => {
    setError('');
    if (!/^[A-Z0-9]{1,3}$/.test(code)) {
      setError('Code must be 1–3 uppercase letters/digits.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await Parse.Cloud.run('updateCurrency', {
          currencyId: currency.id,
          code,
          internalCurrency,
          name,
          isActive
        });
      } else {
        await Parse.Cloud.run('createCurrency', { code, internalCurrency, name });
      }
      onSaved(isEdit ? 'Currency updated successfully' : 'Currency created successfully');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save currency');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#242424', color: '#fff' } }}
    >
      <DialogTitle sx={{ color: '#FFC107' }}>
        {isEdit ? 'Edit Currency' : 'Create Currency'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField
            label="Code (max 3)"
            value={code}
            onChange={handleCodeChange}
            size="small"
            fullWidth
            inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
            helperText="e.g. SC, GC, VC, TC"
            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
            sx={inputSx}
          />

          <FormControl size="small" fullWidth sx={inputSx}>
            <InputLabel>Internal Currency</InputLabel>
            <Select
              value={internalCurrency}
              label="Internal Currency"
              onChange={(e) => setInternalCurrency(e.target.value)}
              sx={{ color: '#fff', '& .MuiSvgIcon-root': { color: '#fff' } }}
            >
              <MenuItem value="SC">SC (Sweep Coins)</MenuItem>
              <MenuItem value="GC">GC (Gold Coins)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            sx={inputSx}
          />

          {isEdit && (
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFC107' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#FFC107'
                    }
                  }}
                />
              }
              label="Active"
            />
          )}

          {currency?.isSystem && (
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
              This is a system currency (internal). Editing affects all operators.
            </Typography>
          )}

          {error && <Typography sx={{ color: '#F44336', fontSize: 13 }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            backgroundColor: '#FFC107',
            color: '#000',
            '&:hover': { backgroundColor: '#FFD54F' }
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#000' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditCurrencyDialog;
